import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, Save, AlertCircle, DollarSign } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Course,
  CreateCourseRequest,
  UpdateCourseRequest,
} from "../../types/course";
import { getDepartments } from "../../services/departments";
import FeeStructureEditor from "./FeeStructureEditor";

interface CourseFormProps {
  course?: Course;
  onSubmit: (data: CreateCourseRequest | UpdateCourseRequest) => Promise<void>;
  onClose: () => void;
  loading?: boolean;
}

const CourseForm: React.FC<CourseFormProps> = ({
  course,
  onSubmit,
  onClose,
  loading = false,
}) => {
  // Fetch departments for dropdown
  const { data: departmentsData, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ["departments"],
    queryFn: async () => {
      const response = await getDepartments(1, 100, undefined, undefined, true);
      return response;
    },
  });

  const [formData, setFormData] = useState({
    name: "",
    level: "Begginner" as "Begginner" | "Intermediate" | "Advanced",
    category: "core" as "core" | "elective" | "practical" | "theory",
    credits: 3,
    description: "",
    duration: 0,
    prerequisites: [] as string[],
    objectives: [] as string[],
    departmentId: "",
    whatsappGroupLink: "",
    isActive: true,
    // Fee Structure fields
    feeComponents: [
      {
        name: "Tuition Fee",
        amount: 0,
        category: "tuition" as
          | "tuition"
          | "books"
          | "uniform"
          | "transport"
          | "exam"
          | "activity"
          | "other",
        description: "",
        isOptional: false,
      },
    ],
    billingFrequency: "monthly",
    perPeriodAmount: null,
    createInvoiceOnEnrollment: false,
    // NOTE: Monthly billing - remove academic term, due date, penalties and installments from course form
  });

  const [newPrerequisite, setNewPrerequisite] = useState("");
  const [newObjective, setNewObjective] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (course) {
      // Extract fee components from existing course fee structure or legacy fees
      let existingFeeComponents = [
        {
          name: "Tuition Fee",
          amount: 0,
          category: "tuition" as
            | "tuition"
            | "books"
            | "uniform"
            | "transport"
            | "exam"
            | "activity"
            | "other",
          description: "",
          isOptional: false,
        },
      ];

      if (course.feeStructure?.components?.length) {
        existingFeeComponents = course.feeStructure.components.map((comp) => ({
          ...comp,
          description: comp.description || "",
          isOptional: comp.isOptional || false,
        }));
      } else if (course.fees && Object.keys(course.fees).length > 0) {
        existingFeeComponents = Object.entries(course.fees)
          .filter(
            ([key, value]) =>
              key !== "totalFee" && typeof value === "number" && value > 0
          )
          .map(([key, value]) => ({
            name: key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase()),
            amount: value as number,
            category: (key.includes("tuition")
              ? "tuition"
              : key.includes("material")
              ? "books"
              : key.includes("lab")
              ? "activity"
              : "other") as
              | "tuition"
              | "books"
              | "uniform"
              | "transport"
              | "exam"
              | "activity"
              | "other",
            description: "",
            isOptional: false,
          }));
      }

      setFormData({
        name: course.name,
        level: course.level,
        category: course.category,
        credits: course.credits,
        description: course.description || "",
        duration:
          course.duration && typeof course.duration === "object"
            ? (course.duration as any).totalHours || 0
            : course.duration || 0,
        prerequisites: course.prerequisites || [],
        objectives: course.objectives || [],
        departmentId: course.departmentId || "",
        whatsappGroupLink: course.whatsappGroupLink || "",
        isActive: course.isActive,
        feeComponents: existingFeeComponents,
        billingFrequency: course.feeStructure?.billingFrequency || 'monthly',
        perPeriodAmount: course.feeStructure?.perPeriodAmount || null,
        createInvoiceOnEnrollment: course.feeStructure?.createInvoiceOnEnrollment || false,      });
    }
  }, [course]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Course name is required";
    }

    if (formData.credits < 1 || formData.credits > 12) {
      newErrors.credits = "Credits must be between 1 and 12";
    }

    if (formData.duration && formData.duration < 0) {
      newErrors.duration = "Duration cannot be negative";
    }

    // Validate WhatsApp group link
    if (
      formData.whatsappGroupLink &&
      !/^(https?:\/\/)?(chat\.whatsapp\.com|wa\.me)\/[^\s]+$/.test(
        formData.whatsappGroupLink
      )
    ) {
      newErrors.whatsappGroupLink = "Invalid WhatsApp group link format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    try {
      // Calculate total amount from fee components
      const totalAmount = formData.feeComponents.reduce(
        (sum, comp) => sum + comp.amount,
        0
      );

      // Monthly billing: no due date or installment validation required
      const installmentSchedule = [];

      // Note: fee components represent per-month amounts when billing monthly


      const submitData = {
        ...formData,
        duration: formData.duration || undefined,
        description: formData.description || undefined,
        prerequisites:
          formData.prerequisites.length > 0
            ? formData.prerequisites
            : undefined,
        objectives:
          formData.objectives.length > 0 ? formData.objectives : undefined,
        departmentId: formData.departmentId || undefined,
        feeStructure:
          totalAmount > 0
            ? {
                components: formData.feeComponents,
                billingFrequency: formData.billingFrequency || "monthly",
                perPeriodAmount:
                  formData.perPeriodAmount !== undefined
                    ? formData.perPeriodAmount
                    : null,
                createInvoiceOnEnrollment: formData.createInvoiceOnEnrollment || false,
              }
            : undefined,
      };

      await onSubmit(submitData);
      toast.success(
        course ? "Course updated successfully" : "Course created successfully"
      );
      onClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save course";
      toast.error(errorMessage);
    }
  };

  const addPrerequisite = () => {
    if (
      newPrerequisite.trim() &&
      !formData.prerequisites.includes(newPrerequisite.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        prerequisites: [...prev.prerequisites, newPrerequisite.trim()],
      }));
      setNewPrerequisite("");
    }
  };

  const removePrerequisite = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      prerequisites: prev.prerequisites.filter((_, i) => i !== index),
    }));
  };

  const addObjective = () => {
    if (
      newObjective.trim() &&
      !formData.objectives.includes(newObjective.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        objectives: [...prev.objectives, newObjective.trim()],
      }));
      setNewObjective("");
    }
  };

  const removeObjective = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-8 w-full max-w-6xl max-h-[95vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-3 rounded-2xl shadow-lg">
              <Save className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900">
              {course ? "Edit Course" : "Create New Course"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-slate-100 transition-all duration-200"
            disabled={loading}
          >
            <X className="h-6 w-6 text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information Section */}
          <div className="border-b border-slate-200 pb-6">
            <h3 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <div className="bg-gradient-to-br from-blue-400 to-indigo-500 p-2 rounded-xl">
                <Save className="h-5 w-5 text-white" />
              </div>
              Basic Information
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-bold text-slate-700">
                    Course Name *
                  </span>
                </label>
                <input
                  type="text"
                  className={`px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-medium text-slate-900 placeholder-slate-400 ${
                    errors.name ? "border-red-400" : "border-slate-200"
                  }`}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter course name"
                  disabled={loading}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-red-500 flex items-center gap-1 font-semibold">
                      <AlertCircle className="h-4 w-4" />
                      {errors.name}
                    </span>
                  </label>
                )}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-bold text-slate-700">
                    Level *
                  </span>
                </label>
                <select
                  className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-semibold text-slate-700"
                  value={formData.level}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      level: e.target.value as
                        | "Begginner"
                        | "Intermediate"
                        | "Advanced",
                    }))
                  }
                  disabled={loading}
                >
                  <option value="Begginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-bold text-slate-700">
                    Category *
                  </span>
                </label>
                <select
                  className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-semibold text-slate-700"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value as
                        | "core"
                        | "elective"
                        | "practical"
                        | "theory",
                    }))
                  }
                  disabled={loading}
                >
                  <option value="core">Core</option>
                  <option value="elective">Elective</option>
                  <option value="practical">Practical</option>
                  <option value="theory">Theory</option>
                </select>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-bold text-slate-700">
                    Department (Optional)
                  </span>
                </label>
                <select
                  className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-semibold text-slate-700"
                  value={formData.departmentId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      departmentId: e.target.value,
                    }))
                  }
                  disabled={loading || isLoadingDepartments}
                >
                  <option value="">Select a department</option>
                  {(() => {
                    const departments = departmentsData?.data || [];
                    return departments.map((dept: any) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name} ({dept.code})
                      </option>
                    ));
                  })()}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Optional: Assign course to a department for better
                  organization
                </p>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-bold text-slate-700">
                    Credits *
                  </span>
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  className={`px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-medium text-slate-900 ${
                    errors.credits ? "border-red-400" : "border-slate-200"
                  }`}
                  value={formData.credits || ""}
                  placeholder="0"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      credits:
                        e.target.value === ""
                          ? ""
                          : parseInt(e.target.value) || 0,
                    }))
                  }
                  disabled={loading}
                />
                {errors.credits && (
                  <label className="label">
                    <span className="label-text-alt text-red-500 flex items-center gap-1 font-semibold">
                      <AlertCircle className="h-4 w-4" />
                      {errors.credits}
                    </span>
                  </label>
                )}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-bold text-slate-700">
                    Duration (hours)
                  </span>
                </label>
                <input
                  type="number"
                  min="0"
                  className={`px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-medium text-slate-900 placeholder-slate-400 ${
                    errors.duration ? "border-red-400" : "border-slate-200"
                  }`}
                  value={formData.duration}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      duration: parseInt(e.target.value),
                    }))
                  }
                  placeholder="Optional"
                  disabled={loading}
                />
                {errors.duration && (
                  <label className="label">
                    <span className="label-text-alt text-red-500 flex items-center gap-1 font-semibold">
                      <AlertCircle className="h-4 w-4" />
                      {errors.duration}
                    </span>
                  </label>
                )}
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-bold text-slate-700">
                    WhatsApp Group Link (Optional)
                  </span>
                </label>
                <input
                  type="url"
                  className={`px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-medium text-slate-900 placeholder-slate-400 ${
                    errors.whatsappGroupLink
                      ? "border-red-400"
                      : "border-slate-200"
                  }`}
                  value={formData.whatsappGroupLink}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      whatsappGroupLink: e.target.value,
                    }))
                  }
                  placeholder="https://chat.whatsapp.com/..."
                  disabled={loading}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Enter the WhatsApp group invite link for this course
                </p>
                {errors.whatsappGroupLink && (
                  <label className="label">
                    <span className="label-text-alt text-red-500 flex items-center gap-1 font-semibold">
                      <AlertCircle className="h-4 w-4" />
                      {errors.whatsappGroupLink}
                    </span>
                  </label>
                )}
              </div>

              {course && (
                <div className="mb-6">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-5 h-5 mt-1 text-green-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                      checked={formData.isActive}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          isActive: e.target.checked,
                        }))
                      }
                      disabled={loading}
                    />
                    <div className="flex-1">
                      <span className="block text-sm font-bold text-slate-700 mb-1">
                        Course Status
                      </span>
                      <span className="text-sm font-medium text-slate-600">
                        {formData.isActive
                          ? "✓ Active - Course is visible and enrollable"
                          : "✗ Inactive - Course is hidden from listings"}
                      </span>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="form-control mt-6 flex flex-col">
              <label className="label">
                <span className="label-text font-bold text-slate-700">
                  Description
                </span>
              </label>
              <textarea
                className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-400 focus:border-transparent font-medium text-slate-900 placeholder-slate-400 h-24 w-full resize-none"
                value={formData.description}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Enter course description"
                disabled={loading}
              />
            </div>

            {/* Prerequisites */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">Prerequisites</span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  className="input input-bordered flex-1"
                  value={newPrerequisite}
                  onChange={(e) => setNewPrerequisite(e.target.value)}
                  placeholder="Add a prerequisite"
                  disabled={loading}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addPrerequisite())
                  }
                />
                <button
                  type="button"
                  onClick={addPrerequisite}
                  className="btn btn-primary"
                  disabled={loading || !newPrerequisite.trim()}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {formData.prerequisites.length > 0 && (
                <div className="space-y-2">
                  {formData.prerequisites.map((prerequisite, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded"
                    >
                      <span className="flex-1">{prerequisite}</span>
                      <button
                        type="button"
                        onClick={() => removePrerequisite(index)}
                        className="btn btn-ghost btn-sm text-red-600"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Course Details Section */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Course Details
            </h3>

            {/* Objectives */}
            <div className="form-control">
              <label className="label">
                <span className="label-text font-medium">
                  Learning Objectives
                </span>
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  className="input input-bordered flex-1"
                  value={newObjective}
                  onChange={(e) => setNewObjective(e.target.value)}
                  placeholder="Add a learning objective"
                  disabled={loading}
                  onKeyPress={(e) =>
                    e.key === "Enter" && (e.preventDefault(), addObjective())
                  }
                />
                <button
                  type="button"
                  onClick={addObjective}
                  className="btn btn-primary"
                  disabled={loading || !newObjective.trim()}
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {formData.objectives.length > 0 && (
                <div className="space-y-2">
                  {formData.objectives.map((objective, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded"
                    >
                      <span className="flex-1">{objective}</span>
                      <button
                        type="button"
                        onClick={() => removeObjective(index)}
                        className="btn btn-ghost btn-sm text-red-600"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Fee Structure Section */}
          <div className="border-b border-gray-200 pb-4">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Fee Structure
            </h3>



            {/* Fee Structure Editor (shared) */}
            <div className="mb-6">
              <FeeStructureEditor
                value={{
                  feeComponents: formData.feeComponents,
                  billingFrequency: formData.billingFrequency,
                  perPeriodAmount: formData.perPeriodAmount ?? null,
                  createInvoiceOnEnrollment: formData.createInvoiceOnEnrollment,
                  isActive: formData.isActive,
                  description: formData.description,
                }}
                onChange={(v) =>
                  setFormData((prev) => ({
                    ...prev,
                    feeComponents: v.feeComponents,
                    billingFrequency: v.billingFrequency || prev.billingFrequency,
                    perPeriodAmount: v.perPeriodAmount ?? prev.perPeriodAmount,
                    createInvoiceOnEnrollment: v.createInvoiceOnEnrollment ?? prev.createInvoiceOnEnrollment,
                    description: v.description ?? prev.description,
                    isActive: v.isActive ?? prev.isActive,
                  }))
                }
              />
            </div>

            {/* Fee Components */}
            <div className="space-y-4">
              <label className="label">
                <span className="label-text font-medium">Fee Components</span>
              </label>

              {formData.feeComponents.map((component, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Component Name *</span>
                      </label>
                      <input
                        type="text"
                        className="input input-bordered input-sm"
                        value={component.name}
                        onChange={(e) => {
                          const updatedComponents = [...formData.feeComponents];
                          updatedComponents[index] = {
                            ...updatedComponents[index],
                            name: e.target.value,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        placeholder="e.g., Tuition Fee"
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Amount *</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input input-bordered input-sm"
                        value={component.amount}
                        onChange={(e) => {
                          const updatedComponents = [...formData.feeComponents];
                          updatedComponents[index] = {
                            ...updatedComponents[index],
                            amount: parseFloat(e.target.value),
                          };
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        placeholder="0.00"
                        disabled={loading}
                        required
                      />
                    </div>

                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Category</span>
                      </label>
                      <select
                        className="select select-bordered select-sm"
                        value={component.category}
                        onChange={(e) => {
                          const updatedComponents = [...formData.feeComponents];
                          updatedComponents[index] = {
                            ...updatedComponents[index],
                            category: e.target.value as any,
                          };
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        disabled={loading}
                      >
                        <option value="tuition">Tuition</option>
                        <option value="books">Books & Materials</option>
                        <option value="uniform">Uniform</option>
                        <option value="transport">Transport</option>
                        <option value="exam">Examination</option>
                        <option value="activity">Activity</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-control mt-4">
                    <label className="label">
                      <span className="label-text">Description</span>
                    </label>
                    <input
                      type="text"
                      className="input input-bordered input-sm"
                      value={component.description || ""}
                      onChange={(e) => {
                        const updatedComponents = [...formData.feeComponents];
                        updatedComponents[index] = {
                          ...updatedComponents[index],
                          description: e.target.value,
                        };
                        setFormData((prev) => ({
                          ...prev,
                          feeComponents: updatedComponents,
                        }));
                      }}
                      placeholder="Optional description"
                      disabled={loading}
                    />
                  </div>

                  <div className="flex items-center justify-between mt-4">
                    <div className="form-control">
                      <label className="label cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm"
                          checked={component.isOptional || false}
                          onChange={(e) => {
                            const updatedComponents = [
                              ...formData.feeComponents,
                            ];
                            updatedComponents[index] = {
                              ...updatedComponents[index],
                              isOptional: e.target.checked,
                            };
                            setFormData((prev) => ({
                              ...prev,
                              feeComponents: updatedComponents,
                            }));
                          }}
                          disabled={loading}
                        />
                        <span className="label-text ml-2">Optional</span>
                      </label>
                    </div>

                    {formData.feeComponents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedComponents =
                            formData.feeComponents.filter(
                              (_, i) => i !== index
                            );
                          setFormData((prev) => ({
                            ...prev,
                            feeComponents: updatedComponents,
                          }));
                        }}
                        className="btn btn-ghost btn-sm text-red-600"
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  setFormData((prev) => ({
                    ...prev,
                    feeComponents: [
                      ...prev.feeComponents,
                      {
                        name: "",
                        amount: 0,
                        category: "other" as
                          | "tuition"
                          | "books"
                          | "uniform"
                          | "transport"
                          | "exam"
                          | "activity"
                          | "other",
                        description: "",
                        isOptional: false,
                      },
                    ],
                  }));
                }}
                className="btn btn-outline btn-sm"
                disabled={loading}
              >
                <Plus className="h-4 w-4" />
                Add Fee Component
              </button>

              {/* Total Fee Display */}
              {formData.feeComponents.some((comp) => comp.amount > 0) && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total Course Fee:</span>
                    <span className="text-xl font-bold text-blue-600">
                      Ksh.
                      {formData.feeComponents
                        .reduce((sum, comp) => sum + comp.amount, 0)
                        .toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

         
          {/* Actions */}
          <div className="flex justify-end gap-4 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="bg-white border-2 border-slate-200 hover:border-slate-300 text-slate-700 px-8 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-white border-2 border-slate-200 hover:border-blue-300 text-slate-700 px-8 py-3 rounded-xl font-bold flex items-center gap-3 hover:shadow-lg hover:scale-105 transition-all duration-300"
              disabled={loading}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-slate-300 border-t-blue-500"></div>
              ) : (
                <>
                  <Save className="h-5 w-5 text-blue-500" />
                  {course ? "Update Course" : "Create Course"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseForm;
